import { type ReactNode, useEffect, useState } from "react";

/**
 * useMounted reports whether the component has completed its first client
 * render. It starts false — the value during static prerender and the very
 * first hydration render — and flips true in an effect, which runs only in the
 * browser after mount. Render output keyed on this value therefore matches
 * between the prerendered markup and the first client render (both see false),
 * so hydration never mismatches.
 */
const useMounted = (): boolean => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return mounted;
};

interface ClientOnlyProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * ClientOnly defers its `children` to the browser. It renders `fallback` during
 * the static prerender and the first client render, then swaps to `children`
 * once mounted. This is the gate for components that must not run on the server:
 * code-split libraries that touch `window` (GraphiQL, Redoc, Monaco) load only
 * after mount, and the prerendered HTML carries the lightweight fallback so the
 * static build never executes them and hydration markup matches.
 */
export const ClientOnly = ({ children, fallback }: ClientOnlyProps): ReactNode => {
  const mounted = useMounted();

  return mounted ? children : fallback;
};
